// Shared date/time parsing for the CSV/Excel import wizards (fixtures, rounds). Every value is
// snapped to "YYYY-MM-DD" / "HH:mm" - the shape rows are edited in and sent to the backend in -
// so nothing downstream ever has to guess a day/month order.

import * as XLSX from 'xlsx'
import moment from 'moment'

// Dates can arrive in whatever shape the spreadsheet was saved in. Year-first and named-month
// dates can't be misread, so they're matched as-is; all-numeric day/month dates (9/12/2026,
// 9.12.2026, 09-12-26, ...) are ambiguous on their own, so they're read in the file's day/month
// order - see detectDateOrder(). Everything is snapped to "YYYY-MM-DD" before it goes anywhere.
const ISO_DATE = /^(\d{4})([-/.])(\d{1,2})\2(\d{1,2})$/
const NUMERIC_DATE = /^(\d{1,2})([-/. ])(\d{1,2})\2(\d{4}|\d{2})$/
const NAMED_MONTH_DATE_FORMATS = [
  'D MMM YYYY',
  'D MMMM YYYY',
  'D-MMM-YYYY',
  'D-MMM-YY',
  'D MMM YY',
  'MMM D YYYY',
  'MMMM D YYYY',
  'MMM D, YYYY',
  'MMMM D, YYYY',
]
const TIME_FORMATS = [
  'h:mm A',
  'h:mm a',
  'h:mmA',
  'h:mma',
  'h:mm:ss A',
  'h.mm A',
  'h.mm a',
  'h A',
  'h a',
  'hA',
  'ha',
  'hh:mm A',
  'hh:mm a',
  'hh:mmA',
  'hh:mma',
  'hh:mm:ss A',
  'hh.mm A',
  'hh.mm a',
  'H:mm',
  'HH:mm',
  'H:mm:ss',
  'HH:mm:ss',
  'H.mm',
  'HH.mm',
]

export const DATE_ORDER_LABELS = { dmy: 'Day / Month / Year', mdy: 'Month / Day / Year' }

// moment() flags overflow (31/02, month 13) as invalid, so this doubles as the range check.
const dateFromParts = (year, month, day) => {
  const m = moment({ year, month: month - 1, date: day })
  return m.isValid() ? m : null
}

const fullYear = (text) => (text.length === 2 ? 2000 + Number(text) : Number(text))

export const parseDateFlexible = (raw, dateOrder) => {
  const str = String(raw ?? '').trim()
  if (!str) return null
  let parts = str.match(ISO_DATE)
  if (parts) return dateFromParts(Number(parts[1]), Number(parts[3]), Number(parts[4]))
  parts = str.match(NUMERIC_DATE)
  if (parts) {
    const first = Number(parts[1])
    const second = Number(parts[3])
    const year = fullYear(parts[4])
    return dateOrder === 'mdy'
      ? dateFromParts(year, first, second)
      : dateFromParts(year, second, first)
  }
  for (const fmt of NAMED_MONTH_DATE_FORMATS) {
    const m = moment(str, fmt, true)
    if (m.isValid()) return m
  }
  return null
}

// A numeric date only gives its order away when one part is over 12 (13/12 must be day-first,
// 9/28 must be month-first), so decide once for the whole file rather than row by row - otherwise
// 9/12 and 13/12 in the same file would be read in opposite orders. With no giveaway either way
// (or a file that contradicts itself) fall back to day-first, the format the template uses, and
// let the user flip it on the Validate step.
export const detectDateOrder = (baseRows) => {
  let dayFirst = false
  let monthFirst = false
  let hasNumeric = false
  baseRows.forEach((r) =>
    [r.start_date, r.end_date].forEach((value) => {
      const parts = String(value ?? '')
        .trim()
        .match(NUMERIC_DATE)
      if (!parts) return
      hasNumeric = true
      if (Number(parts[1]) > 12) dayFirst = true
      if (Number(parts[3]) > 12) monthFirst = true
    }),
  )
  if (dayFirst && monthFirst) return { order: 'dmy', source: 'mixed', hasNumeric }
  if (dayFirst) return { order: 'dmy', source: 'detected', hasNumeric }
  if (monthFirst) return { order: 'mdy', source: 'detected', hasNumeric }
  return { order: 'dmy', source: 'default', hasNumeric }
}

// Excel keeps dates/times as day serials. Read the parts straight off the serial so no timezone
// is involved - going through a JS Date shifts times by minutes (1899 local-mean-time offsets)
// and can shift dates by a day.
export const fromExcelSerial = (value, kind) => {
  const p = XLSX.SSF.parse_date_code(value)
  if (!p) return value
  return kind === 'date'
    ? moment({ year: p.y, month: p.m - 1, date: p.d }).format('YYYY-MM-DD')
    : moment({ hour: p.H, minute: p.M }).format('HH:mm')
}

export const parseTimeFlexible = (raw) => {
  const str = String(raw ?? '').trim()
  if (!str) return null
  for (const fmt of TIME_FORMATS) {
    const m = moment(str, fmt, true)
    if (m.isValid()) return m
  }
  return null
}

// Snaps a date/time to its canonical "YYYY-MM-DD" / "HH:mm" form - the shape the rows are
// edited in and sent to the backend in. Anything unparseable keeps its raw text so the row can
// show what was in the file.
export const normalizeDateField = (raw, dateOrder) => {
  const m = parseDateFlexible(raw, dateOrder)
  if (m) return { text: m.format('YYYY-MM-DD'), moment: m, valid: true }
  return { text: String(raw ?? '').trim(), moment: null, valid: false }
}

export const normalizeTimeField = (raw) => {
  const m = parseTimeFlexible(raw)
  if (m) return { text: m.format('HH:mm'), moment: m, valid: true }
  return { text: String(raw ?? '').trim(), moment: null, valid: false }
}

export const displayDate = (text) =>
  text && moment(text, 'YYYY-MM-DD', true).isValid()
    ? moment(text, 'YYYY-MM-DD').format('D.MM.YYYY')
    : text || '—'

export const displayTime = (text) =>
  text && moment(text, 'HH:mm', true).isValid()
    ? moment(text, 'HH:mm').format('h:mm A')
    : text || '—'
